import { generateChecklistAccountAppLink } from '@breezy/shared'
import { useCallback, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { RichTextAreaSchema } from '../components/PreviewMessage/RichTextAreaField/RichTextAreaField.schema'
import { SendAsEmailFormData } from '../components/PreviewMessage/SendEmailForm/SendEmailForm.schema'
import { getConfig } from '../config'
import { useMessage } from '../utils/antd-utils'

/**
 * Custom hook for attaching appointment checklists to email forms.
 *
 * This hook provides shared functionality for both invoice payment collection
 * and invoice copy sending workflows. It allows users to append completed
 * appointment checklists to email bodies with a single click.
 *
 * The hook formats checklists as HTML links and appends them to the existing email
 * body content, maintaining proper form validation and dirty state tracking.
 *
 * Note: When checklists are selected in the SendInvoiceModal, they will also be
 * attached as PDF files to the email. This hook only adds the links to the email body.
 */

// Simple interface for checklist items as they appear in our GraphQL query
// When using checklist(path: "name"), the checklist field returns the name as a string
export type ChecklistItem = {
  appointmentChecklistInstanceGuid: string
  name: string
}

const config = getConfig()

export const useChecklistAttachment = (emailForm: UseFormReturn<SendAsEmailFormData>, checklists: ChecklistItem[]) => {
  const message = useMessage()
  const [isAttaching, setIsAttaching] = useState(false)

  const attachChecklists = useCallback(async () => {
    if (!checklists.length || isAttaching) return

    setIsAttaching(true)
    try {
      const currentBody = emailForm.getValues('body')

      const checklistLinks = await Promise.all(
        checklists.map(async checklist => {
          const link = await generateChecklistAccountAppLink(
            config.accountAppFullUrl,
            checklist.appointmentChecklistInstanceGuid,
          )
          return `<li><a href="${link}" target="_blank">${String(checklist.name)}</a></li>`
        }),
      )

      const checklistsHtml = `<hr /><p>View the completed checklists online:</p><ul>${checklistLinks.join('')}</ul>`

      emailForm.setValue('body', RichTextAreaSchema.parse(`${currentBody}${checklistsHtml}`), {
        shouldDirty: true,
        shouldValidate: true,
      })
    } catch (error) {
      message.error('Could not attach checklists. Try again later')
    } finally {
      setIsAttaching(false)
    }
  }, [emailForm, checklists, message, isAttaching])

  const canAttachChecklists = checklists.length > 0

  return { attachChecklists, canAttachChecklists, isAttaching }
}
